import React, { useCallback, useState } from "react";
import { isEmpty, isEqual } from "lodash";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";

import { required, requiredCodeRef, getValidation, getDecimalPlaces } from "utils/fieldValidation";
import { useModelCodeLib } from "utils/codes/modelCodeLib";
import { getBaseUnits, getOptions, getUnitOptions } from "utils/fields";
import { useStateSelector } from "utils/field";
import { useSubcomponentDirectionLocked } from "features/Model/Enclosure/_utils/useEffectiveFacingDirection";
import { extractPrebuiltEcmCodes, windowsDefaultCode } from "utils/codes";
import { actions } from "features/Model/Enclosure/_ducks";

import Field from "components/Field";
import InputRow from "components/Input/Row";
import Input from "components/Input";
import InputWithUnits from "components/Input/InputWithUnits";
import Select from "components/Input/Select";
import Tooltip from "components/Tooltip";
import Checkbox from "components/Input/Checkbox";
import SelectWithInput from "components/Input/SelectWithInput";

import Add from "assets/images/icons/JSX/Add";
import Book from "assets/images/icons/JSX/Book";
import Edit from "assets/images/icons/JSX/Edit";

import { useUserDefinedWindowTypeWarning } from "../_utils/useUserDefinedWindowTypeWarning";

import classes from "./style.module.scss";
import globalStyles from "components/globalStyles.module.scss";
import { isOrgCodeRef } from "utils/codes/refs";

const { setInitCode, updateCodeDrawer } = actions;

const windowInsTypeValidation = [required, ...getValidation("windowInsType")];
const erValidation = getValidation("windowER");
const shgcValidation = getValidation("windowSHGC");
const eStarUValValidation = getValidation("windowEStarUValMax");
const eStarERValValidation = getValidation("windowEStarERVal");
const orientationValidation = getValidation("windowDirection");
const numIdenticalValidation = getValidation("windowNumber");
const shutterValidation = getValidation("windowShutterRVal");
const curtainValidation = getValidation("windowCurtainShading");

export const CodeFooter = ({ toggleDrawer, setInitCode, codeType }) => (
    <div
        className={classes.codeFooter}
        onClick={async () => {
            await setInitCode({}, codeType);
            toggleDrawer(true);
        }}
    >
        <span>Create New Code</span>
        <Add />
    </div>
);

const codeSort = (a, b) => {
    if (a.label < b.label) {
        return -1;
    }
    if (a.label > b.label) {
        return 1;
    }
    return 0;
};

const LabelActionComponent = ({ codeRef, handleEdit, codeType }) => (
    <>
        <div
            className={globalStyles.editCode}
            data-tip="Edit Code"
            data-for={codeRef}
            onClick={(event) => handleEdit(event, codeRef, codeType)}
        >
            <Edit />
            <Tooltip id={codeRef} className={globalStyles.fieldTooltip} place="left" />
        </div>
    </>
);

// Window Ins. Type codes found in code library
export const windowInsTypeLibCodesMap = (libCodes, handleEditLibWindowCode) =>
    Object.keys(libCodes)
        .map((codeRef) => {
            const { label = "" } = libCodes[codeRef] || {};
            const [standardCode] = codeRef.split("Window-U");

            return {
                label: label,
                labelIcon: Book,
                labelTag: isOrgCodeRef(codeRef) ? "Organization" : !standardCode ? "User Defined" : "",
                labelActionComponent: () =>
                    handleEditLibWindowCode ? (
                        <LabelActionComponent
                            codeRef={codeRef}
                            handleEdit={handleEditLibWindowCode}
                            codeType={standardCode ? "S" : "U"}
                        />
                    ) : null,
                value: {
                    codeLabel: label,
                    codeRef,
                },
            };
        })
        .sort(codeSort);

// Window Ins. Type codes found in model, filtered by those in library
export const windowInsTypeModelCodesMap = (modelCodes, libCodes, handleEditModelWindowCode) =>
    Object.values(modelCodes)
        .filter(({ codeRef }) => {
            const [component] = codeRef.split("-");
            if (!!libCodes[codeRef]) {
                return (
                    (!libCodes[codeRef] && component === "Window") ||
                    !isEqual(modelCodes[codeRef].layers, libCodes[codeRef].layers)
                );
            }
            return !libCodes[codeRef] && component === "Window";
        })
        .map(({ label, codeRef }) => {
            const [standardCode] = codeRef.split("Window-U");

            return {
                label,
                labelTag: isOrgCodeRef(codeRef) ? "Organization" : !standardCode ? "User Defined" : "",
                labelActionComponent: () => (
                    <LabelActionComponent
                        codeRef={codeRef}
                        handleEdit={handleEditModelWindowCode}
                        codeType={standardCode ? "S" : "U"}
                    />
                ),
                value: {
                    codeLabel: label,
                    codeRef,
                },
            };
        })
        .sort(codeSort);

export const windowInsTypePrebuiltCodesMap = (prebuiltWindowCodes, handleEditPrebuiltWindowCode) =>
    Object.keys(prebuiltWindowCodes)
        .map((codeRef) => {
            const { label = "" } = prebuiltWindowCodes[codeRef] || {};
            const [standardCode] = codeRef.split("Window-U");

            return {
                label: label,
                labelIcon: codeRef === "Window-S-100000" ? null : Add,
                labelTag: isOrgCodeRef(codeRef) ? "Organization" : !standardCode ? "User Defined" : "",
                labelActionComponent: () =>
                    handleEditPrebuiltWindowCode ? (
                        <LabelActionComponent
                            codeRef={codeRef}
                            handleEdit={handleEditPrebuiltWindowCode}
                            codeType={standardCode ? "S" : "U"}
                        />
                    ) : null,
                value: {
                    codeLabel: label,
                    codeRef,
                },
            };
        })
        .sort(codeSort);

const Construction = React.memo(
    ({
        id,
        accessor,
        parentAccessor = accessor,
        change,
        disabledCodeEdit = false,
        selectedUpgrade,
        canBeEditedFromParametric = true,
        isUpgrade = false,
        isCompact = false,
        setInitCode,
        updateCodeDrawer,
        isCommunity = false,
        isParametricAnalysis = false,
        codeRefs = [],
    }) => {
        const [isTouched, setIsTouched] = useState(false); // Hide warning when code is reselected but not yet added to ecmInputMap

        const { Window: { codes: libCodes = {} } = {} } = useModelCodeLib() || {};
        const { calculatingRValue = {} } = useStateSelector(`enclosure`) || {};
        const { currentPackage = {} } = useStateSelector(`upgradeLibrary`) || {};
        const { parametricAnalysis = {} } = useStateSelector(`parametricAnalysis`) || {};
        const { community = {} } = useStateSelector(`community`) || {};
        const { modelData = {} } = useStateSelector(`model`) || {};

        const ecmInputMap = parametricAnalysis?.ecmInputMap || community?.ecmInputMap || {};

        const allEcmInputMapUpgradesArr = ecmInputMap
            ? Object.keys(ecmInputMap)
                  .map((key) =>
                      Object.entries(ecmInputMap[key])
                          .filter(([k, _]) => k !== "None")
                          .map(([k, v]) => ({ [k]: v }))
                  )
                  .flat()
            : [];

        const allEcmInputMapUpgrades = allEcmInputMapUpgradesArr.reduce((acc, curr) => ({ ...acc, ...curr }), {});

        const modelCodes =
            allEcmInputMapUpgrades[selectedUpgrade]?.packageValues?.codes ||
            currentPackage?.upgrades?.[selectedUpgrade]?.codes ||
            modelData?.codes ||
            {};

        const modelUnits = modelData?.uiSettings?.primaryUnits || {};

        const { codeRef: selectedCodeRef, codeLabel: selectedLabel } = useStateSelector(`${accessor}.windowType`) || {};
        const selectedRValue = useStateSelector(`${accessor}.windowType_effRVal`) || 0;
        const windowShgc = useStateSelector(`${accessor}.shgc`) || 0;
        const windowEr = useStateSelector(`${accessor}.er`) || 0;
        const rValUnits = useStateSelector(`${accessor}.windowType_u`);
        const enerStar = useStateSelector(`${accessor}.energyStar`);
        const { [id]: { windowInsType: isCalculatingRValue = false } = {} } = calculatingRValue;

        const prebuiltWindowCodes = isCommunity ? { ...windowsDefaultCode } : {};

        //Determine if selectedCodeRef is in modelCodes (attached properly)
        const modelCodesContainsSelected =
            Object.keys(modelCodes).includes(selectedCodeRef) || selectedCodeRef === "Window-S-100000" || isTouched;

        const userDefinedWindowTypeWarning = useUserDefinedWindowTypeWarning({ accessor, modelCodes, libCodes });

        const { value: eStarERVal = 0 } = useStateSelector(`${accessor}.energyStarType`) || {};
        const { major: h2kMajor, minor: h2kMinor } = useStateSelector(`model.modelData.h2kVersion`) || {};

        const shutterUnits = useStateSelector(`${accessor}.shading.shutterRValue_u`);
        const curtainUnits = useStateSelector(`${accessor}.shading.curtainShading_u`);

        const { id: windowFacingId } = useStateSelector(`${accessor}.facingDirection`) || {};
        const { id: parentFacingId } = useStateSelector(`${parentAccessor}.facingDirection`) || {};

        const handleTypeChange = useCallback(
            ({ codeRef = "" }) => {
                if (!codeRef) return;
                const modelMatch = modelCodes[codeRef] || {};
                if (!isEmpty(modelMatch)) {
                    change(`${accessor}.windowType_nomRVal`, modelMatch.nominalRValue || 0);
                    change(`${accessor}.windowType_effRVal`, modelMatch.nominalRValue || 0);
                    change(`${accessor}.shgc`, modelMatch.shgc || 0);
                    change(`${accessor}.er`, modelMatch.er || 0);
                } else {
                    const {
                        nominalRValue = 0,
                        shgc = 0,
                        er = 0,
                    } = libCodes[codeRef] || prebuiltWindowCodes[codeRef] || {};

                    change(`${accessor}.windowType_nomRVal`, nominalRValue || 0);
                    change(`${accessor}.windowType_effRVal`, nominalRValue || 0);
                    change(`${accessor}.shgc`, shgc || 0);
                    change(`${accessor}.er`, er || 0);

                    if (isCommunity || isParametricAnalysis) {
                        setIsTouched(true);
                    } else {
                        change(`model.modelData.codes.${codeRef}`, { ...libCodes[codeRef], codeRef });
                    }
                }

                change(`model.stashedCodes.Window.${accessor.split(".").slice(-1)[0]}`, codeRef);
            },
            [libCodes, prebuiltWindowCodes, modelCodes, change, accessor, isCommunity, isParametricAnalysis]
        );

        const handleEditModelWindowCode = useCallback(
            async (event, codeRef, codeType) => {
                event.stopPropagation(); //Don't let click event bubble up to parent
                const initCodeType = codeType === "U" ? "windowUDef" : "windowType";
                const code = modelCodes[codeRef];

                if (!code) {
                    console.warn(`Code ${codeRef} not found in modelCodes`);
                    return;
                }

                await setInitCode(code, initCodeType);
                updateCodeDrawer({
                    isOpen: true,
                    codeName: "Window",
                    isEditing: true,
                    componentType: "Window",
                    codeType: codeType,
                    windowAccessor: accessor,
                    componentId: id,
                    id: codeRef,
                    lastEdited: code.lastEdited || "",
                });
            },
            [accessor, id, modelCodes, setInitCode, updateCodeDrawer]
        );

        const handleEditLibWindowCode = disabledCodeEdit
            ? null
            : useCallback(
                  async (event, codeRef, codeType) => {
                      event.stopPropagation(); //Don't let click event bubble up to parent
                      const initCodeType = codeType === "U" ? "windowUDef" : "windowType";
                      if (!libCodes[codeRef]) {
                          console.warn(`Code ${codeRef} not found in libCodes`);
                          return;
                      }
                      const code = { ...libCodes[codeRef], codeRef: codeRef, isLibCode: true };
                      await setInitCode(code, initCodeType);
                      updateCodeDrawer({
                          isOpen: true,
                          codeName: "Window",
                          isEditing: true,
                          componentType: "Window",
                          codeType: codeType,
                          windowAccessor: accessor,
                          componentId: id,
                          id: codeRef,
                          lastEdited: code.lastEdited || "",
                      });
                  },
                  [accessor, id, libCodes, setInitCode, updateCodeDrawer]
              );

        const handleEditPrebuiltWindowCode = isCommunity
            ? useCallback(
                  async (event, codeRef, codeType) => {
                      event.stopPropagation();
                      const initCodeType = codeType === "U" ? "windowUDef" : "windowType";
                      const code = { ...prebuiltWindowCodes[codeRef], codeRef: codeRef, isPrebuiltEcmCode: true };
                      await setInitCode(code, initCodeType);
                      updateCodeDrawer({
                          isOpen: true,
                          codeName: "Window",
                          isEditing: true,
                          componentType: "Window",
                          codeType: codeType,
                          windowAccessor: accessor,
                          componentId: id,
                          id: codeRef,
                          lastEdited: code.lastEdited || "",
                      });
                  },
                  [accessor, id, prebuiltWindowCodes, setInitCode, updateCodeDrawer]
              )
            : null;

        const isDisableWindowDir = useSubcomponentDirectionLocked("window", accessor, parentAccessor);

        return (
            <>
                <InputRow gridTemplate={isCompact ? "1fr 1fr 1fr 1fr 1fr" : "31.85% 23.25% 23.25%"} compact={isCompact}>
                    <Field
                        component={Select}
                        type="number"
                        name={`${accessor}.windowType`}
                        options={[
                            ...windowInsTypeModelCodesMap(
                                modelCodes,
                                { ...libCodes, ...prebuiltWindowCodes },
                                handleEditModelWindowCode
                            ),
                            ...(isCommunity
                                ? windowInsTypePrebuiltCodesMap(prebuiltWindowCodes, handleEditPrebuiltWindowCode)
                                : []),
                            ...windowInsTypeLibCodesMap(libCodes, handleEditLibWindowCode),
                        ]}
                        label="Window Type"
                        placeholder="Choose Window Type"
                        searchPlaceholder="Search Window Codes"
                        search={true}
                        footer={() => (
                            <CodeFooter
                                toggleDrawer={(value) =>
                                    updateCodeDrawer({
                                        isOpen: value,
                                        codeName: "Window",
                                        isEditing: false,
                                        componentType: "Window",
                                        codeType: "S",
                                        windowAccessor: accessor,
                                        componentId: id,
                                    })
                                }
                                setInitCode={setInitCode}
                                codeType="windowInsType"
                            />
                        )}
                        className={classes.windowType}
                        isLoading={isCalculatingRValue}
                        onChange={handleTypeChange}
                        validate={requiredCodeRef}
                        disabled={!canBeEditedFromParametric}
                        compact={isCompact}
                        warning={
                            !modelCodesContainsSelected
                                ? "codeDataMissing"
                                : userDefinedWindowTypeWarning || null
                        }
                    />
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name={`${accessor}.windowType_effRVal`}
                        label="Effective R-Value"
                        placeholder={0.0}
                        disabled={true}
                        validate={windowInsTypeValidation}
                        setTouched={true}
                        decimals={getDecimalPlaces("windowInsType")}
                        change={change}
                        setValue={selectedRValue}
                        units={{
                            base: getBaseUnits("windowInsType", modelUnits),
                            options: getUnitOptions("thermalResistance"),
                            selected: rValUnits,
                            unitType: "thermalResistance",
                            accessor: `${accessor}.windowType_u`,
                        }}
                        info={
                            "This value is an estimate based on a standardized window size. HOT2000 will automatically update this value based on the actual window size when running simulations."
                        }
                        isLoading={isCalculatingRValue}
                        compact={isCompact}
                    />
                    <Field
                        component={Checkbox}
                        name={`${accessor}.energyStar`}
                        label="Energy Star"
                        large
                        className={classes.enerStar}
                        type="checkbox"
                        disabled={!canBeEditedFromParametric}
                        compact={isCompact}
                    />
                    {isCompact && (
                        <>
                            <Field
                                component={Select}
                                name={`${accessor}.facingDirection`}
                                options={getOptions({ fieldKey: "windowDirection" }).map((option) => ({
                                    ...option,
                                    disabled: codeRefs.includes(option.value?.codeRef),
                                }))}
                                label="Facing Direction"
                                placeholder="Choose Facing Direction"
                                validate={orientationValidation}
                                disabled={isDisableWindowDir}
                                compact
                                compassProps={{
                                    facingId: windowFacingId,
                                    disabled: isDisableWindowDir,
                                    rotationOptions: {
                                        0: 180,
                                        1: 135,
                                        2: 90,
                                        3: 45,
                                        4: 0,
                                        5: -45,
                                        6: -90,
                                        7: -135,
                                    },
                                }}
                            />
                            <Field
                                component={Input}
                                type="number"
                                name={`${accessor}.numIdentical`}
                                label="Number of Identical Windows"
                                placeholder={0}
                                validate={numIdenticalValidation}
                                decimals={getDecimalPlaces("windowNumber")}
                                info={
                                    "In addition to size and construction details, identical windows must all face the same direction."
                                }
                                compact
                            />
                        </>
                    )}
                </InputRow>
                <InputRow
                    gridTemplate={isCompact ? "1fr 1fr 1fr 1fr 1fr" : "1fr 1fr 1fr"}
                    compact={isCompact}
                    lastRow={isCompact}
                >
                    <Field
                        component={Input}
                        type="number"
                        name={`${accessor}.er`}
                        label="Window ER"
                        placeholder={0.0}
                        validate={erValidation}
                        setValue={windowEr}
                        decimals={getDecimalPlaces("windowER")}
                        disabled
                        info={
                            "This value is an estimate based on a standardized window size. HOT2000 will automatically update this value based on the actual window size when running simulations."
                        }
                        isLoading={isCalculatingRValue}
                        compact={isCompact}
                    />
                    <Field
                        component={Input}
                        type="number"
                        name={`${accessor}.shgc`}
                        label="SHGC"
                        placeholder={0.0}
                        validate={shgcValidation}
                        decimals={getDecimalPlaces("windowSHGC")}
                        setValue={windowShgc}
                        disabled
                        info={
                            "This value is an estimate based on a standardized window size. HOT2000 will automatically update this value based on the actual window size when running simulations."
                        }
                        isLoading={isCalculatingRValue}
                        compact={isCompact}
                    />
                    {enerStar && h2kMinor >= 11 && (
                        <Field
                            component={SelectWithInput}
                            inputData={{
                                triggerValue: "er",
                                inputLabel: "ER",
                                inputType: "number",
                                inputPlaceholder: 0,
                            }}
                            disabled={!canBeEditedFromParametric}
                            name={`${accessor}.energyStarType`}
                            options={[
                                {
                                    label: "U-Value > 1.22",
                                    value: { id: "uValue", uValueMin: 1.22, uValueMax: 0, value: 0 },
                                },
                                {
                                    label: "U-Value > 1.05 and <= 1.22",
                                    value: { id: "uValue", uValueMin: 1.05, uValueMax: 1.22, value: 0 },
                                },
                                {
                                    label: "U-Value <= 1.05",
                                    value: { id: "uValue", uValueMin: 0, uValueMax: 1.05, value: 0 },
                                },
                                {
                                    label: "Energy Rating",
                                    value: { id: "er", uValueMin: 0, uValueMax: 0, value: 0 },
                                },
                            ]}
                            label="Energy Star Type"
                            placeholder="Choose Energy Star Type"
                            validate={eStarERValValidation}
                            warning={eStarERVal > 62 ? "highWindowER" : ""}
                            compact={isCompact}
                        />
                    )}
                    {isCompact && (
                        <>
                            <Field
                                component={InputWithUnits}
                                type="number"
                                name={`${accessor}.shading.shutterRValue`}
                                label="Shutter R-Value"
                                placeholder={0.0}
                                quantity
                                validate={shutterValidation}
                                decimals={getDecimalPlaces("windowShutterRVal")}
                                change={change}
                                units={{
                                    base: getBaseUnits("windowShutterRVal", modelUnits),
                                    options: getUnitOptions("thermalResistance"),
                                    selected: shutterUnits,
                                    unitType: "thermalResistance",
                                    accessor: `${accessor}.shading.shutterRValue_u`,
                                }}
                                info={"Use the default value (0)"}
                                compact
                            />
                            <Field
                                component={InputWithUnits}
                                type="number"
                                name={`${accessor}.shading.curtainShading`}
                                label="Curtain Shading"
                                placeholder="0"
                                quantity
                                validate={curtainValidation}
                                decimals={getDecimalPlaces("windowCurtainShading")}
                                change={change}
                                units={{
                                    base: getBaseUnits("windowCurtainShading", modelUnits),
                                    options: getUnitOptions("fraction"),
                                    selected: curtainUnits,
                                    unitType: "fraction",
                                    accessor: `${accessor}.shading.curtainShading_u`,
                                }}
                                info={"Use the default value (1)"}
                                compact
                            />
                        </>
                    )}
                </InputRow>
            </>
        );
    }
);

const mapStateToProps = createStructuredSelector({});

const mapDispatchToProps = (dispatch) => ({
    setInitCode: async (code, type) => {
        return dispatch(setInitCode(code, type));
    },
    updateCodeDrawer: (updates) => dispatch(updateCodeDrawer({ updates })),
});

export default connect(mapStateToProps, mapDispatchToProps)(Construction);
